import React, { useEffect, useState } from 'react';
import { getUser, setSession } from '../services/auth';
import api from '../api';
import './Settings.css';

const Settings = () => {
  const user = getUser();
  const telegramId = user?.telegram_id;

  const [tab, setTab] = useState('profile');
  const [username, setUsername] = useState(user?.username || '');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  const [categories, setCategories] = useState([]);
  const [newCat, setNewCat] = useState({ name: '', emoji: '', color: '#9184d9' });

  const [banksMaster, setBanksMaster] = useState([]);
  const [userBanks, setUserBanks] = useState([]);
  const [bankForm, setBankForm] = useState({ bankName: 'HSBC', balance: '' });

  const loadCategories = async () => {
    const { data } = await api.get('/api/categories', { params: { telegramId } });
    setCategories(data.categories || []);
  };

  const loadBanks = async () => {
    const [m, u] = await Promise.all([
      api.get('/api/categories/banks-master'),
      api.get('/api/categories/user-banks', { params: { telegramId } })
    ]);
    setBanksMaster(m.data.banks || []);
    setUserBanks(u.data.banks || []);
    const first = (m.data.banks || [])[0]?.name;
    if (first) setBankForm((f) => ({ ...f, bankName: f.bankName || first }));
  };

  useEffect(() => {
    if (telegramId) {
      loadCategories();
      loadBanks();
    }
  }, [telegramId]);

  const flash = (text, type) => {
    setMessage(text);
    setMessageType(type);
    if (type === 'success') setTimeout(() => { setMessage(''); setMessageType(''); }, 3000);
  };

  const onChangeUsername = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post('/api/auth/change-username', { telegramId, username });
      if (data.success) {
        flash('Username updated successfully', 'success');
        setSession(localStorage.getItem('authToken'), { ...user, username }, true);
      } else {
        flash(data.message || 'Failed to update username', 'error');
      }
    } catch (err) {
      flash(err.response?.data?.message || 'Failed to update username', 'error');
    }
  };

  const onCreateCategory = async (e) => {
    e.preventDefault();
    if (!newCat.name.trim()) {
      flash('Category name is required', 'error');
      return;
    }
    await api.post('/api/categories', { telegramId, ...newCat });
    setNewCat({ name: '', emoji: '', color: '#9184d9' });
    await loadCategories();
  };

  const onUpdateCategory = async (id, patch) => {
    await api.put(`/api/categories/${id}`, patch);
    await loadCategories();
  };

  const onEditCategory = (c) => {
    const name = window.prompt('Name', c.name);
    if (name == null) return;
    const emoji = window.prompt('Emoji', c.emoji || '');
    if (emoji == null) return;
    const color = window.prompt('Color', c.color || '#9184d9');
    if (color == null) return;
    onUpdateCategory(c.id, { name, emoji, color });
  };

  const onDeleteCategory = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    await api.delete(`/api/categories/${id}`);
    await loadCategories();
  };

  const onAddBank = async (e) => {
    e.preventDefault();
    if (!bankForm.bankName) {
      flash('Bank is required', 'error');
      return;
    }
    if (bankForm.balance === '') {
      flash('Balance is required', 'error');
      return;
    }
    const now = new Date();
    const istOffset = 5.5 * 60 * 60 * 1000;
    const istTime = new Date(now.getTime() + istOffset);
    const formattedDate = istTime.toISOString();
    try {
      await api.post('/api/categories/user-banks', {
        telegramId,
        bankName: bankForm.bankName,
        balance: Number(bankForm.balance),
        asOf: formattedDate
      });
      flash(`${bankForm.bankName} balance updated to ₹${bankForm.balance}`, 'success');
      setBankForm({ bankName: bankForm.bankName, balance: '' });
      await loadBanks();
    } catch (error) {
      flash('Failed to update bank balance', 'error');
    }
  };

  const formatAsOf = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'UTC'
    }) + ' IST';
  };

  return (
    <div className="settings-page">
      <p className="set-eyebrow">PocketGuru · settings</p>
      <h1 className="set-title">Settings</h1>
      <p className="set-lead">Account, banks, and categories. Opened from the nav gear.</p>

      {message && <div className={`alert alert-${messageType}`}>{message}</div>}

      <div className="set-tabs" role="tablist">
        {[
          ['profile', 'Profile'],
          ['banks', 'Banks'],
          ['categories', 'Categories']
        ].map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={`set-tab ${tab === key ? 'on' : ''}`}
            onClick={() => setTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'profile' && (
        <div className="set-panel">
          <h2>Profile</h2>
          <p className="sub">Public handle used across the web app and bot.</p>
          <p className="meta-line">
            Telegram ID <code>{telegramId}</code> · read-only
          </p>
          <form onSubmit={onChangeUsername} className="set-form-row">
            <div className="field-wrap">
              <label>Username</label>
              <input
                className="input"
                value={username}
                onChange={(e) => setUsername(e.target.value.toLowerCase())}
                placeholder="Enter username"
              />
              <div className="hint">Unique · letters, numbers, underscores only</div>
            </div>
            <button className="btn" type="submit">
              Save username
            </button>
          </form>
        </div>
      )}

      {tab === 'banks' && (
        <div className="set-panel">
          <h2>Banks</h2>
          <p className="sub">Balances drive Dashboard total cash. Recorded as of current IST.</p>
          <div className="set-bank-cards">
            {userBanks.length === 0 ? (
              <p className="small">No banks yet — add a balance below.</p>
            ) : (
              userBanks.map((b) => (
                <div className="set-bank-card" key={b.user_bank_id}>
                  <div className="name">{b.display_name || b.bank_name}</div>
                  <div className="bal">
                    {b.current_balance != null
                      ? `₹${Number(b.current_balance).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
                      : '—'}
                  </div>
                  <div className="asof">As of {formatAsOf(b.balance_as_of)}</div>
                </div>
              ))
            )}
          </div>
          <form onSubmit={onAddBank} className="set-form-row">
            <div className="field-wrap" style={{ flex: '0 0 160px' }}>
              <label>Bank</label>
              <select
                className="input"
                value={bankForm.bankName}
                onChange={(e) => setBankForm({ ...bankForm, bankName: e.target.value })}
              >
                {banksMaster.map((b) => (
                  <option key={b.id} value={b.name}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="field-wrap">
              <label>Current balance (₹)</label>
              <input
                className="input"
                type="number"
                step="0.01"
                value={bankForm.balance}
                onChange={(e) => setBankForm({ ...bankForm, balance: e.target.value })}
                placeholder="0.00"
              />
            </div>
            <button className="btn" type="submit">
              Update balance
            </button>
          </form>
        </div>
      )}

      {tab === 'categories' && (
        <div className="set-panel">
          <h2>Categories</h2>
          <p className="sub">Add or edit categories. Kind/group fields come later.</p>
          <form onSubmit={onCreateCategory} className="set-form-row" style={{ marginBottom: 16 }}>
            <div className="field-wrap">
              <label>Name</label>
              <input
                className="input"
                value={newCat.name}
                onChange={(e) => setNewCat({ ...newCat, name: e.target.value })}
                placeholder="e.g. Groceries"
              />
            </div>
            <div className="field-wrap" style={{ flex: '0 0 100px' }}>
              <label>Emoji</label>
              <input
                className="input"
                value={newCat.emoji}
                onChange={(e) => setNewCat({ ...newCat, emoji: e.target.value })}
                placeholder="🛒"
              />
            </div>
            <div className="field-wrap" style={{ flex: '0 0 120px' }}>
              <label>Color</label>
              <input
                className="input"
                type="color"
                value={newCat.color || '#9184d9'}
                onChange={(e) => setNewCat({ ...newCat, color: e.target.value })}
                style={{ padding: 4, height: 42 }}
              />
            </div>
            <button className="btn" type="submit">
              Add category
            </button>
          </form>
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th></th>
                  <th>Name</th>
                  <th>Color</th>
                  <th>Source</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((c) => (
                  <tr key={c.id}>
                    <td style={{ fontSize: 18 }}>{c.emoji || ''}</td>
                    <td>{c.name}</td>
                    <td>
                      <span
                        className="set-swatch"
                        style={{ background: c.color || 'transparent' }}
                      />
                      {c.color || '—'}
                    </td>
                    <td>
                      <span className={`set-badge ${c.is_default ? '' : 'muted'}`}>
                        {c.is_default ? 'Default' : 'Custom'}
                      </span>
                    </td>
                    <td>
                      <div className="table-actions" style={{ justifyContent: 'flex-end' }}>
                        <button type="button" className="btn secondary" onClick={() => onEditCategory(c)}>
                          Edit
                        </button>
                        <button type="button" className="btn danger" onClick={() => onDeleteCategory(c.id)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
